import { db } from "../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, increment, getDoc, query, where } from "firebase/firestore";

const couponsCollection = collection(db, "coupons");

export const generateCoupon = async (code, discount, discountType, expiryDate, maxUses = 1, userMaxUses = 1) => {
	// Convert to uppercase for case-insensitive comparison
	const codeUpper = code.toUpperCase();

	// Check for existing coupon with same code
	const q = query(couponsCollection, where("code", "==", codeUpper));
	const querySnapshot = await getDocs(q);

	if (!querySnapshot.empty) {
		throw new Error("Coupon code already exists");
	}

	const couponData = {
		code: codeUpper,
		discount,
		discountType,
		expiryDate,
		maxUses,
		userMaxUses,
		useCount: 0,
		userUses: {},
		createdAt: new Date().toISOString(),
		active: true,
	};

	try {
		const docRef = await addDoc(couponsCollection, couponData);
		return { id: docRef.id, ...couponData };
	} catch (error) {
		console.error("Failed to generate coupon:", error);
		throw error;
	}
};

export const listCoupons = async () => {
	try {
		const querySnapshot = await getDocs(couponsCollection);
		return querySnapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
	} catch (error) {
		console.error("Failed to fetch coupons:", error);
		throw error;
	}
};

export const applyCoupon = async (couponCode, phoneNumber) => {
	try {
		// Query coupon by code
		const q = query(couponsCollection, where("code", "==", couponCode.toUpperCase()));
		const querySnapshot = await getDocs(q);

		if (querySnapshot.empty) {
			throw new Error("Coupon not found");
		}

		// There should be only one coupon with the same code
		const couponDoc = querySnapshot.docs[0];
		const coupon = couponDoc.data();
		const couponId = couponDoc.id;

		// Check if coupon has reached max uses
		if (coupon.useCount >= coupon.maxUses) {
			throw new Error("Coupon has reached its maximum uses");
		}

		// Check if user has reached their max uses for this coupon
		const userUses = coupon.userUses[phoneNumber] || 0;
		if (userUses >= coupon.userMaxUses) {
			throw new Error("You have reached the maximum uses for this coupon");
		}

		// Check if coupon is active
		if (!coupon.active) {
			throw new Error("Coupon is not active");
		}

		// Update coupon usage
		const couponRef = doc(db, "coupons", couponId);
		await updateDoc(couponRef, {
			useCount: increment(1),
			[`userUses.${phoneNumber}`]: increment(1),
		});

		return true;
	} catch (error) {
		console.error("Failed to apply coupon:", error);
		throw error;
	}
};

export const deleteCoupon = async (couponId) => {
	try {
		const couponRef = doc(db, "coupons", couponId);
		await deleteDoc(couponRef);
		return true;
	} catch (error) {
		console.error("Failed to delete coupon:", error);
		throw error;
	}
};

export const toggleCouponStatus = async (couponId, active) => {
	try {
		const couponRef = doc(db, "coupons", couponId);
		await updateDoc(couponRef, { active });
		return true;
	} catch (error) {
		console.error("Failed to toggle coupon status:", error);
		throw error;
	}
};

export const searchCouponsByCode = async (searchTerm) => {
	try {
		// Convert searchTerm to uppercase for case-insensitive search
		const upperSearchTerm = searchTerm.toUpperCase();

		// Query coupons where code contains the search term
		const q = query(couponsCollection, where("code", ">=", upperSearchTerm), where("code", "<=", upperSearchTerm + "\uf8ff"));
		const querySnapshot = await getDocs(q);

		return querySnapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
	} catch (error) {
		console.error("Failed to search coupons:", error);
		throw error;
	}
};

export const updateCoupon = async (couponId, updateData) => {
	try {
		const couponRef = doc(db, "coupons", couponId);
		await updateDoc(couponRef, updateData);
		return true;
	} catch (error) {
		console.error("Failed to update coupon:", error);
		throw error;
	}
};
